import type { ReactElement } from "react";
import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { IRadioSelectItem } from "app/pages/sales-intelligence/common-components/form-fields";
import { AdChannel } from "app/pages/sales-intelligence/domain/leads/types/enums";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { IRange } from "app/pages/sales-intelligence/types/range";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { DOLLAR_PREFIX } from "../../../constants";
import type FindAdSpendVolumeFilter from "../../../models/AdSpendVolumeFilter/AdSpendVolumeFilter";
import type { IAdSpendVolumeFilterDto } from "../../../types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "../../../utils/i18n";
import { formatMoney, formatNumber } from "../../../utils/numberFormatters";
import { RangeSelectWithClearField } from "../../RangeSelectWithClearField/RangeSelectWithClearField";
import RangeSlider from "../../RangeSlider/RangeSlider";
import { FilterContentContainer, StyledVerticalRadioSelect } from "../styles";

const TRANSLATION_PREFIX = "si.lead_gen_filters.ad_spend_volume";

const AdSpendVolumeFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindAdSpendVolumeFilter>): ReactElement | null => {
    const { translate } = useTranslate();

    const [selectedChannel, setSelectedChannel] = useState<AdChannel>(value?.channel ?? AdChannel.Total);

    const handleChannelSelect = (channel: AdChannel) => {
        setSelectedChannel(channel);

        const hasRangeFilters = value?.spend || value?.activeCampaigns || value?.adsNumber;
        const hasExistingDifferentChannel = value?.channel && value.channel !== channel;

        if (hasRangeFilters || hasExistingDifferentChannel) {
            onFilterUpdate({ ...value, channel });
        }
    };

    const handleClear = (key: keyof IAdSpendVolumeFilterDto) => () => {
        const updated = { ...value };
        delete updated[key];
        onFilterUpdate(updated);
    };

    const handleChange = (key: keyof IAdSpendVolumeFilterDto, range: Partial<IRange<number>>) => {
        onFilterUpdate({
            ...value,
            channel: selectedChannel,
            [key]: range,
        });
    };

    const handleSliderChange = (key: keyof IAdSpendVolumeFilterDto, range: Partial<IRange<number>>) => {
        if (is.undefined(range.from) && is.undefined(range.to)) {
            handleClear(key)();
            return;
        }

        handleChange(key, range);
    };

    const radioItems = useMemo(
        (): IRadioSelectItem[] =>
            filter.options.channels.map((channel) => ({
                id: channel,
                text: translate(`${TRANSLATION_PREFIX}.channel.${channel}`),
            })),
        [filter.options.channels],
    );

    const channel = value?.channel ?? selectedChannel;
    return (
        <FilterContentContainer>
            <StyledVerticalRadioSelect
                size="sm"
                selected={channel}
                onSelect={handleChannelSelect}
                items={radioItems}
            />
            <RangeSelectWithClearField
                field="spend"
                buckets={filter.options.adSpendBuckets}
                formatter={formatMoney}
                placeholderPrefix={DOLLAR_PREFIX}
                value={value?.spend}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="activeCampaigns"
                buckets={filter.options.adsActiveCampaigns}
                formatter={formatNumber}
                value={value?.activeCampaigns}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="adsNumber"
                buckets={filter.options.adsNumber}
                formatter={formatNumber}
                value={value?.adsNumber}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSlider
                name="mobileShare"
                title={translate(leadsSearchFiltersI18nKeys.filter("mobileShare"))}
                options={filter.options.displayAdsShare}
                value={value?.mobileShare}
                onAfterChange={(val: Partial<IRange<number>>) => handleSliderChange("mobileShare", val)}
            />
        </FilterContentContainer>
    );
};

export default AdSpendVolumeFilter;
